/**
 * Welcome Storage Utility
 * Quản lý flag "user đã xem Beta Welcome" trong localStorage.
 *
 * Theo tài liệu `CANSLIM_Beta_Welcome_Getting_Started_Page_FE_Spec_FIX1`:
 * - Trang/modal Welcome hiển thị SAU khi user đăng ký thành công
 *   HOẶC lần đầu đăng nhập trên trình duyệt bằng Facebook/Google.
 * - Sau khi user đã xem (đóng modal hoàn tất), KHÔNG hiện lại lần sau
 *   trên cùng trình duyệt.
 *
 * Cờ `justRegistered` được set tạm thời ngay sau khi đăng ký / đăng nhập
 * social thành công để App.jsx biết cần mở modal ngay. Sau khi modal đã
 * hiển thị xong, cờ này được xóa để tránh hiện lại khi refresh trang.
 *
 * Cờ `hasSeenWelcome` (persistent) dùng để xác định user đã từng thấy
 * modal chưa — nếu rồi thì lần đăng nhập sau trên cùng trình duyệt sẽ
 * KHÔNG hiện lại.
 */

const JUST_REGISTERED_KEY = 'beta_welcome_just_registered';
const HAS_SEEN_KEY = 'beta_welcome_has_seen';
const USER_ID_KEY = 'beta_welcome_user_id';

const isStorageAvailable = () => {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
};

export const welcomeStorage = {
  /**
   * Đánh dấu user vừa đăng ký / đăng nhập social thành công.
   * App.jsx sẽ đọc cờ này để mở modal ngay khi user được nhận diện.
   *
   * @param {string|number} userId - ID user để tránh lặp lại cho user khác
   *   trên cùng trình duyệt (multi-account).
   */
  markJustRegistered(userId) {
    if (!isStorageAvailable()) return;
    try {
      window.localStorage.setItem(JUST_REGISTERED_KEY, '1');
      if (userId !== undefined && userId !== null) {
        window.localStorage.setItem(USER_ID_KEY, String(userId));
      }
    } catch {
      // ignore quota / privacy mode errors
    }
  },

  /**
   * Lấy và xóa cờ "vừa đăng ký". Một lần đọc = một lần hiện modal.
   */
  consumeJustRegistered() {
    if (!isStorageAvailable()) return false;
    try {
      const value = window.localStorage.getItem(JUST_REGISTERED_KEY);
      if (value === '1') {
        window.localStorage.removeItem(JUST_REGISTERED_KEY);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  },

  /**
   * Chỉ đọc cờ "vừa đăng ký" mà KHÔNG xóa.
   * Dùng khi cần check nhiều lần trước khi consume (vd: effect chạy nhiều lần
   * trước khi user state được set xong).
   */
  peekJustRegistered() {
    if (!isStorageAvailable()) return false;
    try {
      const value = window.localStorage.getItem(JUST_REGISTERED_KEY);
      return value === '1';
    } catch {
      return false;
    }
  },

  /**
   * User đã xem modal Welcome trên trình duyệt này chưa.
   * Trả về true nếu cùng userId đã xem rồi.
   */
  hasSeenWelcome(userId) {
    if (!isStorageAvailable()) return false;
    try {
      const seen = window.localStorage.getItem(HAS_SEEN_KEY);
      if (seen !== '1') return false;
      // Nếu userId khác với user đã xem trước đó, coi như chưa xem
      // (ví dụ: logout rồi đăng nhập account khác trên cùng browser)
      if (userId !== undefined && userId !== null) {
        const seenUserId = window.localStorage.getItem(USER_ID_KEY);
        if (seenUserId && seenUserId !== String(userId)) {
          return false;
        }
      }
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Đánh dấu user đã xem Welcome (persist vĩnh viễn trên browser).
   */
  markSeen(userId) {
    if (!isStorageAvailable()) return;
    try {
      window.localStorage.setItem(HAS_SEEN_KEY, '1');
      if (userId !== undefined && userId !== null) {
        window.localStorage.setItem(USER_ID_KEY, String(userId));
      }
    } catch {
      // ignore
    }
  },

  /**
   * Reset toàn bộ flag — dùng cho testing hoặc khi cần reset onboarding.
   */
  reset() {
    if (!isStorageAvailable()) return;
    try {
      window.localStorage.removeItem(JUST_REGISTERED_KEY);
      window.localStorage.removeItem(HAS_SEEN_KEY);
      window.localStorage.removeItem(USER_ID_KEY);
    } catch {
      // ignore
    }
  },
};

export default welcomeStorage;
